import React from 'react';
import styled from 'styled-components';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const Container = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 16px;
    padding: 8px 16px;
    font-size: 13px;
    color: #666;
    border-top: 1px solid #f0f0f0;
`;

const PageInfo = styled.span`
    font-variant-numeric: tabular-nums;
`;

const PageSizeSelect = styled.select`
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    padding: 4px 8px;
    font-size: 13px;
    color: #333;
    background: #fff;
    cursor: pointer;
`;

const NavButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    background: #fff;
    cursor: pointer;
    color: #333;

    &:hover:not(:disabled) {
        background: #fafafa;
    }

    &:disabled {
        opacity: 0.3;
        cursor: default;
    }
`;

const NavGroup = styled.div`
    display: flex;
    gap: 4px;
`;

interface TablePaginationProps {
    page: number;
    pageSize: number;
    totalRows: number;
    pageSizeOptions?: number[];
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
}

export function TablePagination({
    page,
    pageSize,
    totalRows,
    pageSizeOptions = [10, 25, 50],
    onPageChange,
    onPageSizeChange,
}: TablePaginationProps) {
    const start = totalRows === 0 ? 0 : page * pageSize + 1;
    const end = Math.min((page + 1) * pageSize, totalRows);
    const totalPages = Math.ceil(totalRows / pageSize);

    return (
        <Container>
            <span>Rows per page:</span>
            <PageSizeSelect
                value={pageSize}
                onChange={e => {
                    onPageSizeChange(Number(e.target.value));
                }}
            >
                {pageSizeOptions.map(opt => (
                    <option key={opt} value={opt}>
                        {opt}
                    </option>
                ))}
            </PageSizeSelect>
            <PageInfo>
                {start}–{end} of {totalRows.toLocaleString()}
            </PageInfo>
            <NavGroup>
                <NavButton disabled={page === 0} onClick={() => onPageChange(page - 1)} aria-label="Previous page">
                    <ChevronLeft size={16} />
                </NavButton>
                <NavButton disabled={page >= totalPages - 1} onClick={() => onPageChange(page + 1)} aria-label="Next page">
                    <ChevronRight size={16} />
                </NavButton>
            </NavGroup>
        </Container>
    );
}
